import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Plus, RefreshCw, FileText, CheckCircle2, Clock, AlertCircle, XCircle, BookCheck,
  Banknote
} from 'lucide-react';

import { apPaymentApi }  from '@/api/apPayment.api';
import { useContextScope, scopeFromSession } from '@/hooks/useContextScope';
import { ScopeChips } from '@/components/lists/ScopeChips';
import {
  FilterRow, ListSearch, DateRangeFilter, StatusPills, StatusTiles,
} from '@/components/lists/ListFilters';
import { ListPager } from '@/components/lists/ListPager';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { AppRoutes }     from '@/constants/routes';
import { Button }        from '@/components/ui/Button';
import { IconButton }    from '@/components/ui/IconButton';
import { Select }        from '@/components/ui/Select';
import { PageHeader }    from '@/components/layout/PageHeader';
import { ListTable, type ListColumn } from '@/components/lists/ListTable';
import { cn }            from '@/utils/cn';
import type { RootState } from '@/state/store';

import type { ApPaymentListDto, ApPaymentFilterDto } from '@/types/apPayment.types';

// ── Payment method labels (inline) ────────────────────────────────────────────

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  Cash:          'Cash',
  Cheque:        'Cheque',
  BankTransfer:  'Bank Transfer',
  CreditCard:    'Credit Card',
  DebitCard:     'Debit Card',
  OnlineBanking: 'Online Banking',
  Other:         'Other',
};

// ── Workflow status display helpers ───────────────────────────────────────────

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  Draft:           'Draft',
  Submitted:       'Submitted',
  PendingApproval: 'Pending',
  Approved:        'Approved',
  Rejected:        'Rejected',
  Posted:          'Posted',
  Cancelled:       'Cancelled',
  Reversed:        'Reversed',
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  Draft:           'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  Submitted:       'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  PendingApproval: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  Approved:        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Rejected:        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  Posted:          'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  Cancelled:       'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  Reversed:        'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

const StatusIcon = ({ status }: { status: string }) => {
  const props = { size: 14, className: 'shrink-0' };
  switch (status) {
    case 'Draft':        return <FileText    {...props} className={`${props.className} text-gray-500 dark:text-gray-400`} />;
    case 'Submitted':
    case 'PendingApproval': return <Clock   {...props} className={`${props.className} text-blue-500`} />;
    case 'Approved':     return <CheckCircle2 {...props} className={`${props.className} text-green-500`} />;
    case 'Posted':       return <BookCheck  {...props} className={`${props.className} text-emerald-600 dark:text-emerald-400`} />;
    case 'Rejected':     return <XCircle    {...props} className={`${props.className} text-red-500`} />;
    default:             return <AlertCircle {...props} className={`${props.className} text-amber-500`} />;
  }
};

// ── AP Payment List Page ──────────────────────────────────────────────────────

export const ApPaymentListPage = () => {
  const navigate              = useNavigate();
  const { hasPermission }     = usePermission();
  const canCreate             = hasPermission(Permissions.AP_PAYMENT_CREATE);

  const session = useSelector((s: RootState) => s.session.session);

  const [items,   setItems]   = useState<ApPaymentListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const statusCounts = {
    Draft:     items.filter(i => i.workflowStatus === 'Draft').length,
    Submitted: items.filter(i => i.workflowStatus === 'Submitted' || i.workflowStatus === 'PendingApproval').length,
    Approved:  items.filter(i => i.workflowStatus === 'Approved').length,
    Posted:    items.filter(i => i.workflowStatus === 'Posted').length,
    Rejected:  items.filter(i => i.workflowStatus === 'Rejected').length,
  };

  const [filter, setFilter] = useState<ApPaymentFilterDto>(() => ({
    pageNumber:     1,
    pageSize,
    sortBy:         'paymentDate',
    sortDescending: true,
    ...scopeFromSession(session),
  }));

  const scope = useContextScope(s => {
    setFilter(f => ({ ...f, ...s, pageNumber: 1 }));
    setPage(1);
  });

  const [search, setSearch] = useState('');

  const load = useCallback(async (f: ApPaymentFilterDto) => {
    setLoading(true);
    try {
      const res = await apPaymentApi.getList(f);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to load AP payments', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(filter); }, [filter, load]);

  const handleSearch = () => {
    setFilter(f => ({ ...f, paymentNumber: undefined, search: search || undefined, pageNumber: 1 }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilter(f => ({ ...f, pageNumber: newPage }));
  };

  /** The pills and tiles decide whether a click selects or clears; this just applies it. */
  const handleStatusFilter = (status: string | undefined) => {
    setFilter(f => ({ ...f, workflowStatus: status, pageNumber: 1 }));
    setPage(1);
  };

  const totalPages = Math.ceil(total / pageSize);

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const columns: ListColumn<ApPaymentListDto>[] = [
    { key: 'no',   header: 'Payment #', className: 'font-mono font-semibold text-brand-700 dark:text-brand-400', render: r => r.paymentNumber },
    { key: 'date', header: 'Date', className: 'text-slate-700 dark:text-slate-300', render: r => fmtDate(r.paymentDate) },
    { key: 'party', header: 'Supplier', render: r => (
        <>
          <div className="font-medium text-slate-900 dark:text-white">{r.supplierCode}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[180px]">{r.supplierName}</div>
        </>
      ) },
    { key: 'method', header: 'Method', render: r => (
        <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded text-xs">
          {PAYMENT_METHOD_LABELS[r.paymentMethod] ?? r.paymentMethod}
        </span>
      ) },
    { key: 'amt',  header: 'Amount', align: 'right', className: 'font-mono text-slate-900 dark:text-white',
      render: r => r.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 }) },
    { key: 'ccy',  header: 'Ccy',       className: 'font-mono text-xs text-slate-500 dark:text-slate-400', render: r => r.currencyCode },
    { key: 'ref',  header: 'Reference', className: 'text-xs text-slate-500 dark:text-slate-400 truncate max-w-[120px]', render: r => r.reference ?? '—' },
    { key: 'st',   header: 'Status', align: 'center', render: r => (
        <div className="flex items-center justify-center gap-1.5">
          <StatusIcon status={r.workflowStatus} />
          <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', PAYMENT_STATUS_COLORS[r.workflowStatus] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
            {PAYMENT_STATUS_LABELS[r.workflowStatus] ?? r.workflowStatus}
          </span>
        </div>
      ) },
  ];

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <PageHeader
        icon={Banknote}
        title="AP Payments"
        subtitle={`${total.toLocaleString()} total records`}
        actions={
          <div className="flex items-center gap-2">
            <IconButton label="Refresh" icon={<RefreshCw size={15} />} onClick={() => void load(filter)} />
            {canCreate && (
              <Button leftIcon={<Plus size={15} />} onClick={() => navigate(AppRoutes.AP_PAYMENT_NEW)}>New Payment</Button>
            )}
          </div>
        }
      />

      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 pb-4">
        {/* ── Active context banner ─────────────────────────────────────────── */}
        <ScopeChips chips={scope.chips} canReset={scope.canReset} onReset={scope.reset} />

        <StatusTiles
          active={filter.workflowStatus}
          onChange={handleStatusFilter}
          tiles={[
            { label: 'Draft',    status: 'Draft',     count: statusCounts.Draft,     icon: <FileText     size={14} />, color: 'text-slate-500 dark:text-gray-400',   bg: 'bg-slate-50 dark:bg-slate-700/40',     ring: 'ring-slate-200'   },
            { label: 'Pending',  status: 'Submitted', count: statusCounts.Submitted, icon: <Clock        size={14} />, color: 'text-blue-600 dark:text-blue-400',    bg: 'bg-blue-50 dark:bg-blue-900/20',       ring: 'ring-blue-200'    },
            { label: 'Approved', status: 'Approved',  count: statusCounts.Approved,  icon: <CheckCircle2 size={14} />, color: 'text-green-600 dark:text-green-400',   bg: 'bg-green-50 dark:bg-green-900/20',     ring: 'ring-green-200'   },
            { label: 'Posted',   status: 'Posted',    count: statusCounts.Posted,    icon: <BookCheck    size={14} />, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', ring: 'ring-emerald-200' },
            { label: 'Rejected', status: 'Rejected',  count: statusCounts.Rejected,  icon: <XCircle      size={14} />, color: 'text-red-500',     bg: 'bg-red-50 dark:bg-red-900/20',         ring: 'ring-red-200'     },
          ]}
        />

        <FilterRow>
          <ListSearch
            value={search}
            onChange={setSearch}
            onSubmit={handleSearch}
            placeholder="Search payment #, supplier…"
          />

          <StatusPills
            statuses={['Draft', 'Submitted', 'Approved', 'Posted', 'Rejected', 'Cancelled', 'Reversed'] as const}
            active={filter.workflowStatus}
            onChange={handleStatusFilter}
            label={s => PAYMENT_STATUS_LABELS[s]}
            activeClass={s => PAYMENT_STATUS_COLORS[s] ?? ''}
          />

          <Select
            aria-label="Payment method"
            value={filter.paymentMethod ?? ''}
            onChange={e => {
              const val = e.target.value;
              setFilter(f => ({ ...f, paymentMethod: val || undefined, pageNumber: 1 }));
              setPage(1);
            }}
            placeholder="All Methods"
            options={Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({ value, label }))}
            fieldClassName="w-44"
          />

          <DateRangeFilter
            from={filter.dateFrom}
            to={filter.dateTo}
            onChange={({ from, to }) => {
              setFilter(f => ({ ...f, dateFrom: from, dateTo: to, pageNumber: 1 }));
              setPage(1);
            }}
          />
        </FilterRow>
      </div>

      <div className="flex-1 overflow-auto">
        <ListTable
          rows={items}
          columns={columns}
          rowKey={r => r.id}
          onRowClick={r => navigate(AppRoutes.AP_PAYMENT_DETAIL.replace(':id', r.id))}
          loading={loading}
          empty={{
            icon: Banknote,
            message: 'No payments found',
            action: canCreate
              ? <Button variant="ghost" size="sm" onClick={() => navigate(AppRoutes.AP_PAYMENT_NEW)}>Create your first payment</Button>
              : undefined,
          }}
        />
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <ListPager
          page={page}
          totalPages={totalPages}
          totalCount={total}
          onChange={handlePageChange}
          noun="payments"
        />
      )}
    </div>
  );
};
